import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Stack,
  StackProps,
  Button,
  Typography,
  Grid,
  FormControlLabel,
  Switch,
  Alert,
  IconButton,
  ListItem,
} from "@mui/material";
import FormSection from "../../../Forms/FormSection/form-section";

import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import ConfigFormIcon from "../../ConfigFormIcon";
import FormAutocomplete from "../../../Forms/FormAutocomplete/form-autocomplete";
import { useAppSelector } from "../../../../Redux/redux-store";
import { GetSectionIsValid, SetFieldIsValid } from "../../../../Redux/Reducers/redux-slice-form-validation";
import { DbOrderBasketItemInfos } from "../../../../Core/Api/Types/db-order-basket-item-infos";
import { TranskodeGenericOptionsProfile } from "../../../../Core/Api/Types/transkode-generic-options-profile";
import {
  BasketShowOnlyAvailable,
  GetBasket,
  ProductField,
  SettingRootField,
  TgopField,
  UpdateItemProductRootOnBasket,
  UpdateItemSettingsRootOnBasket,
  UpdateItemSettingsTgopOnBasket,
  UpdateItemStatusOnBasket,
} from "../../../../Redux/Reducers/redux-slice-basket";
import { GetAudioLabel, GetSubtitleLabel } from "../../../../Utils/Helpers/IntelliTranskode/intellitranskode-helpers";
import { useDispatch, useSelector } from "react-redux";
import { WatermarkTemplates } from "../../../../Core/Api/Types/watermark-templates";
import FormInputText from "../../../Forms/FormInputText/form-input-text";
import { UserHasAcl } from "../../../../Utils/helpers";
import { AclTypes } from "../../../../Core/Api/Types/acl-types";
import { DbStorageInfos } from "../../../../Core/Api/Types/db-storage-infos";
import { MailingListDto } from "../../../../Core/Api/Types/get-mailing-list-response";
import { TranskodeFieldOrder } from "../../../../Core/Api/Types/transkode-field-order";
import useCustomSnackbar from "../../../../Utils/Hooks/useCustomSnackbar";
import { OrderProductType } from "../../../../Core/Api/Types/order-product-type";
import ConfigCommonSectionFormDelivery from "../../ConfigCommon/config-common-section-form-delivery";
import { BasketItemStatus } from "../../../../Core/Api/Types/basket-item-status";
import { ObjectType } from "../../../../Core/Api/Types/object-type";
import { DbAssetObjectInfos } from "../../../../Core/Api/Types/db-asset-object-infos";
import { assetService } from "../../../../Core/Api/services";
import ConfigCommonSectionFormClips from "../../ConfigCommon/config-common-section-form-clips";
import { ObjectLayer } from "../../../../Core/Api/Types/object-layer";
import { GetMetadataByTagName } from "../../../../Core/Utils/asset-object-utils";
import ConfigCommonSectionFormStitching from "../../ConfigCommon/config-common-section-form-stitching";
import GlobalConst from "../../../../Core/Utils/Constants/globals";
import ConfigCommonSectionFormScheduling from "../../ConfigCommon/config-common-section-form-scheduling";
import { PictureOptionCouple } from "../../../../Core/Api/Types/picture-option-couple";
import ConfigCommonSectionFormNexguardWatermark from "../../ConfigCommon/config-common-section-form-nexguard-watermark";
import ConfigCommonSectionFormAssetsVisible from "../../ConfigCommon/config-common-section-form-assets-visible";
import { TranskodeVideoCodec } from "../../../../Core/Api/Types/transkode-video-codec";
import { useTranslation } from "react-i18next";
import { AudioMixLanguageCouple } from "../../../../Core/Api/Types/audio-mix-language-couple";
import { Add, DeleteOutline } from "@mui/icons-material";

interface ConfigTranscodeFormProps extends StackProps {
  item: DbOrderBasketItemInfos;
  formid: string;
  storages: DbStorageInfos[];
  maillists: MailingListDto[];
}

const ConfigTranscodeForm: React.FC<ConfigTranscodeFormProps> = (props) => {
  const { t } = useTranslation();
  const { item, formid: formId, storages, maillists } = props;
  const dispatch = useDispatch();
  const toggleAvailableOnly = useAppSelector(BasketShowOnlyAvailable);
  const enqueueCustomSnackbar = useCustomSnackbar();
  const basket = useSelector(GetBasket);

  const [clips, setClips] = useState<DbAssetObjectInfos[]>([]);

  const textConfigRequired = t("common:configurationRequired");
  const textConfigOptional = t("form:statusText.configurationIsOptional");

  const productType: OrderProductType = item.selectedProduct?.type!;
  const simplifiedForm =
    productType === OrderProductType.DcpReferenceFileSimple ||
    productType === OrderProductType.DcpReferenceFileStitched ||
    productType === OrderProductType.TranscodeImf ||
    productType === OrderProductType.TranscodeDcp;

  // Tgop correctly typed here
  // Item is updated automatically as it's passed as prop from parent and from redux orignally
  const tgop: TranskodeGenericOptionsProfile =
    productType === OrderProductType.TranscodeGeneric
      ? item.selectedProduct?.settings?.tgop
      : item.selectedProduct?.settings;

  // Update tgop or asinput product (depending of item type) object into redux
  const handleUpdateTranscodeParam = (field: TgopField, value: any) => {
    switch (productType) {
      case OrderProductType.TranscodeGeneric:
        dispatch(UpdateItemSettingsTgopOnBasket({ itemId: item.id, field: field, value: value }));
        break;
      case OrderProductType.TranscodeGenericSingleFile:
      case OrderProductType.DcpReferenceFileSimple:
      case OrderProductType.DcpReferenceFileStitched:
      case OrderProductType.TranscodeSequence:
        // For facility, reuse Tgop field and map them to Root Field
        let rootField: SettingRootField = SettingRootField.AudioOnly;

        switch (field) {
          case TgopField.FileFormat:
            rootField = SettingRootField.FileFormat;
            break;
          case TgopField.Fps:
            rootField = SettingRootField.Fps;
            break;
          case TgopField.DropFrame:
            rootField = SettingRootField.DropFrame;
            break;
          case TgopField.ScanType:
            rootField = SettingRootField.ScanType;
            break;
          case TgopField.BurnTimecode:
            rootField = SettingRootField.BurnTimecode;
            break;
          case TgopField.AudioOnly:
            rootField = SettingRootField.AudioOnly;
            break;
          case TgopField.ExplodeAudioChannels:
            rootField = SettingRootField.ExplodeAudioChannels;
            break;
          case TgopField.RewrapOnly:
            rootField = SettingRootField.RewrapOnly;
            break;
          case TgopField.VideoFilterDesature:
            rootField = SettingRootField.VideoFilterDesature;
            break;
          case TgopField.OverlayText:
            rootField = SettingRootField.OverlayText;
            break;
        }

        dispatch(UpdateItemSettingsRootOnBasket({ itemId: item.id, field: rootField, value: value }));
        break;
    }
  };

  // Apply to all other item of same type the overlay text
  const applyToAllOverlayText = () => {
    basket.items
      ?.filter(
        (x) =>
          x.selectedProduct !== null &&
          (x.selectedProduct.type === OrderProductType.TranscodeGeneric ||
            x.selectedProduct.type === OrderProductType.TranscodeGenericSingleFile)
      )
      .forEach((x) => {
        dispatch(
          UpdateItemProductRootOnBasket({
            itemId: x.id,
            field: ProductField.WatermarkTextTemplate,
            value: item.selectedProduct?.watermarkTemplate,
          })
        );
        if (x.selectedProduct?.type === OrderProductType.TranscodeGeneric)
          dispatch(
            UpdateItemSettingsTgopOnBasket({
              itemId: x.id,
              field: TgopField.OverlayText,
              value: tgop.overlayTextPlaceholder,
            })
          );
        else
          dispatch(
            UpdateItemSettingsRootOnBasket({
              itemId: x.id,
              field: SettingRootField.OverlayText,
              value: item.selectedProduct?.settings.overlayTextPlaceholder,
            })
          );
      });
  };

  // Update product root base object into redux
  const handleUpdateProductRoot = (field: ProductField, value: any) => {
    dispatch(UpdateItemProductRootOnBasket({ itemId: item.id, field: field, value: value }));
  };

  // Toggle audio only or video A/V muxed file
  const handleToggleAudioOnly = () => {
    handleUpdateTranscodeParam(TgopField.FileFormat, null);
    handleUpdateTranscodeParam(TgopField.AudioOnly, null);
    dispatch(
      SetFieldIsValid({
        formId: formId,
        formSectionId: "sectionFileSpec",
        fieldId: "fileFormat",
        fieldIsValid: false,
        fieldErrorMessage: t("field:errorMessage.reselectValueAfterToggle"),
      })
    );
  };

  // Toggle audio only or video A/V muxed file
  const handleToggleProxySource = useCallback(() => {
    handleUpdateTranscodeParam(TgopField.FileFormat, null);
    handleUpdateProductRoot(ProductField.Proxy, !item.selectedProduct?.proxy);
    dispatch(
      SetFieldIsValid({
        formId: formId,
        formSectionId: "sectionFileSpec",
        fieldId: "fileFormat",
        fieldIsValid: false,
        fieldErrorMessage: t("field:errorMessage.reselectValueAfterToggle"),
      })
    );
  }, [item.selectedProduct?.proxy]);

  const isSubtitleConfigValid = useMemo(() => {
    if (!tgop) return false;

    let bad =
      tgop.subtitleSidecarFiles &&
      tgop.subtitleSidecarFiles.selectedValues &&
      tgop.subtitleSidecarFiles.selectedValues.filter(
        (x) =>
          x.warns !== null &&
          x.warns!.filter((w) => w.outputFormat === tgop.fileFormatSubtitleSidecar?.selectedValue?.format).length > 0
      ).length > 0;
    if (bad) dispatch(UpdateItemStatusOnBasket({ itemId: item.id, status: BasketItemStatus.New }));

    return !bad;
  }, [tgop]);

  const getOptionLabel = (value: PictureOptionCouple) => {
    const lang = value.isLocalized ? value.language?.label + ` (${value.language?.value}) - ` : "TEXTLESS - ";
    return lang + value.format?.format + (value.format?.isOriginal ? " (OAR)" : "") + " - " + value.contentSubType;
  };

  // Form validator
  const formSectionFileSpecs = useAppSelector(GetSectionIsValid(formId, "sectionFileSpec"));
  const formSectionAudio = useAppSelector(GetSectionIsValid(formId, "sectionAudio"));
  const formSectionPicture = useAppSelector(GetSectionIsValid(formId, "sectionPicture"));
  const formSectionSubtitle = useAppSelector(GetSectionIsValid(formId, "sectionSubtitle"));
  const formSectionSecurity = useAppSelector(GetSectionIsValid(formId, "sectionSecurity"));

  useEffect(() => {
    // Auto enable audio only toggle to help user
    if (item.assetInfos && item.assetInfos?.type === ObjectType.Audio && !tgop.isAudioOnly) handleToggleAudioOnly();

    // Load clips from content
    if (productType === OrderProductType.TranscodeGeneric && item.objectLayer !== ObjectLayer.Clip) {
      if (!item?.episodeNb)
        assetService.GetClipsByObjectId(item?.selectedContent?.id!).then((response) => {
          if (response.results) setClips(response.results);
        });
      else
        assetService
          .GetClipsByObjectIdWithEpisodeNumber(item?.selectedContent?.id!, item?.episodeNb)
          .then((response) => {
            if (response.results) setClips(response.results);
          });
    } else if (productType === OrderProductType.TranscodeGenericSingleFile) {
      assetService.GetClipsByObjectId(item?.objectId!).then((response) => {
        if (response.results) setClips(response.results);
      });
    }
  }, []);

  const fileSpecsStatusText = useMemo(() => {
    if (!tgop) return textConfigRequired;

    const contents: string[] = [];

    // File format
    if (tgop.fileFormat?.selectedValue) {
      let content = tgop.fileFormat?.selectedValue.label || "";
      if (!tgop.fileFormat?.selectedValue.isAvailable) content += "(N/A)";
      contents.push(content);
    }

    // Framerate
    if (tgop.frameRate?.selectedValue) {
      if (tgop.frameRate?.selectedValue.value === 0)
        contents.push(
          "As Input" +
            (productType === OrderProductType.TranscodeGenericSingleFile
              ? " (" + GetMetadataByTagName(item.assetInfos, "techFrameRate")?.value + ")"
              : "")
        );
      else contents.push(`${tgop.frameRate?.selectedValue?.value} fps`);
    }

    if (tgop.videoFieldOrder?.selectedValue)
      contents.push(tgop.videoFieldOrder?.selectedValue.fieldOrder?.toString() || "");

    if (tgop.isAudioOnly) contents.push("Audio only");

    if (tgop.explodeAudioChannels) contents.push("Explode channels");

    if (tgop.frameRate?.selectedValue?.dropFrame) contents.push("Drop frame");

    if (contents.length) return contents.join(GlobalConst.FORM_SECTION_SUMMARY_DELIMITER);

    return textConfigRequired;
  }, [tgop]);

  const audioMappingStatusText = useMemo(() => {
    if (!tgop) return textConfigRequired;

    const contents: string[] = [];

    if (tgop.audioMixes?.selectedValues)
      contents.push(...tgop.audioMixes?.selectedValues.map((value) => GetAudioLabel(value)));

    if (tgop.avoidVarispeed) contents.push("Avoid varispeed");

    if (contents.length) return contents.join(GlobalConst.FORM_SECTION_SUMMARY_DELIMITER);

    if (tgop.isAudioOnly && (!tgop.audioMixes?.selectedValues || tgop.audioMixes?.selectedValues.length === 0))
      return textConfigRequired;

    return textConfigOptional;
  }, [tgop]);

  const pictureOptionsStatusText = useMemo(() => {
    if (!tgop) return textConfigRequired;
    const contents: string[] = [];

    if (contents.length) return contents.join(GlobalConst.FORM_SECTION_SUMMARY_DELIMITER);

    if (tgop.picture?.selectedValue) contents.push(getOptionLabel(tgop.picture?.selectedValue));

    if (tgop.burnTimecode) contents.push("Print Timecode");

    if (tgop.videoFilters?.desaturation) contents.push("Apply Black & White filter");

    if (contents.length) return contents.join(GlobalConst.FORM_SECTION_SUMMARY_DELIMITER);

    if (formSectionPicture === undefined) return t("common:configured");

    return textConfigRequired;
  }, [tgop, formSectionPicture]);

  const subtitleOptionStatusText = useMemo(() => {
    if (!tgop) return textConfigRequired;

    const contents: string[] = [];

    if (tgop.subtitleBurned?.selectedValue) contents.push(GetSubtitleLabel(tgop.subtitleBurned?.selectedValue));

    if (tgop.subtitleSidecarFiles?.selectedValues)
      contents.push(...tgop.subtitleSidecarFiles?.selectedValues.map((value) => GetSubtitleLabel(value)));

    if (tgop.fileFormatSubtitleSidecar?.selectedValue)
      contents.push(tgop.fileFormatSubtitleSidecar!.selectedValue!.label || "N/A");

    if (contents.length) return contents.join(GlobalConst.FORM_SECTION_SUMMARY_DELIMITER);

    if (!isSubtitleConfigValid) return textConfigRequired;

    return textConfigOptional;
  }, [tgop, isSubtitleConfigValid]);

  const securityOptionsStatusText = useMemo(() => {
    if (!tgop) return textConfigRequired;

    const contents: string[] = [];

    if (item.selectedProduct!.watermarkTemplate) contents.push(item.selectedProduct!.watermarkTemplate);

    if (tgop.overlayTextPlaceholder || item.selectedProduct?.settings.overlayTextPlaceholder)
      contents.push(tgop.overlayTextPlaceholder || item.selectedProduct?.settings.overlayTextPlaceholder);

    if (contents.length) return contents.join(GlobalConst.FORM_SECTION_SUMMARY_DELIMITER);

    if (
      item.selectedProduct!.watermarkTemplate ||
      item.selectedProduct?.type === OrderProductType.DcpReferenceFileSimple ||
      item.selectedProduct?.type === OrderProductType.DcpReferenceFileStitched
    )
      return textConfigRequired;
    return textConfigOptional;
  }, [item, tgop]);

  const audioMixesOptions: (AudioMixLanguageCouple & { originalId?: number })[] = useMemo(() => {
    let audioMixes: AudioMixLanguageCouple[] = tgop.audioMixes?.values || [];

    if (tgop.audioMixes?.selectedValues) {
      let i = 0;
      const time = Math.floor(Date.now() / 1000);
      const duplicates = tgop.audioMixes?.selectedValues.map((audioMix) => {
        i++;
        return {
          ...audioMix,
          originalId: audioMix.id,
          id: time + i,
        };
      });

      audioMixes = [...audioMixes, ...duplicates];
    }

    return audioMixes.filter((x) => !toggleAvailableOnly || (toggleAvailableOnly && x.isAvailable));
  }, [tgop.audioMixes, toggleAvailableOnly]);

  // Security but that shouldn't happen
  if (!tgop && productType === OrderProductType.TranscodeGeneric)
    return <Typography color="error">{t("alert:error.somethingWrongWithConfig")}</Typography>;

  return (
    <form>
      <Stack spacing={1.5} width={"100%"} {...props}>
        {!simplifiedForm && (
          <FormSection
            icon={<ConfigFormIcon icon="settings" />}
            title={t("form:section.title.technicalSpecs")}
            status={formSectionFileSpecs ? "complete" : "warning"}
            statusText={fileSpecsStatusText}
          >
            {!tgop.rewrapOnly && (
              <Grid container spacing={3}>
                <Grid item md={6}>
                  <FormAutocomplete
                    label={t("field:label.fileFormat")}
                    options={
                      tgop.fileFormat?.values!.filter(
                        (x) =>
                          (!toggleAvailableOnly || x.isAvailable) &&
                          ((!tgop.isAudioOnly && !x.isAudioOnly) || (tgop.isAudioOnly && x.isAudioOnly)) &&
                          (!item.selectedProduct?.proxy ||
                            (item.selectedProduct?.proxy && x.codec === TranskodeVideoCodec.H264))
                      )!
                    }
                    value={tgop.fileFormat?.selectedValue}
                    multiple={false}
                    onChange={(v) => {
                      handleUpdateTranscodeParam(TgopField.FileFormat, v);
                    }}
                    getOptionLabel={(value: any) => value.label + (!value.isAvailable ? " (N/A)" : "")}
                    isOptionEqualToValue={(option, value) => option.baseId === value.baseId}
                    filterOptions={(x, s) =>
                      x.filter((a) => a.label?.toLowerCase().includes(s.inputValue.toLowerCase()))
                    }
                    validationProps={{
                      formId: formId,
                      formSectionId: "sectionFileSpec",
                      fieldId: "fileFormat",
                      required: true,
                      initialState: false,
                    }}
                  />
                </Grid>

                <Grid item md={3}>
                  <FormAutocomplete
                    label={t("common:frameRate")}
                    options={
                      tgop.frameRate?.values!.filter(
                        (x) => !toggleAvailableOnly || (toggleAvailableOnly && x.isAvailable)
                      )!
                    }
                    value={tgop.frameRate?.selectedValue}
                    multiple={false}
                    onChange={(v) => {
                      if (!v) {
                        handleUpdateTranscodeParam(TgopField.Fps, null);
                        return;
                      }

                      if (
                        tgop.videoFieldOrder?.selectedValue !== null &&
                        tgop.videoFieldOrder?.selectedValue !== undefined &&
                        tgop.videoFieldOrder?.selectedValue.fieldOrder === TranskodeFieldOrder.Interlaced &&
                        (v.value === 23.98 ||
                          v.value === 24 ||
                          (v.value === 0 &&
                            productType === OrderProductType.TranscodeGenericSingleFile &&
                            (GetMetadataByTagName(item.assetInfos, "techFrameRate")?.value === 23.98 ||
                              GetMetadataByTagName(item.assetInfos, "techFrameRate")?.value === 24)))
                      ) {
                        enqueueCustomSnackbar(t("alert:warning.frameRateIncompatible"), "warning");
                        handleUpdateTranscodeParam(TgopField.Fps, undefined);
                        return;
                      }

                      handleUpdateTranscodeParam(TgopField.Fps, v);
                    }}
                    getOptionLabel={(value: any) =>
                      value.value === 0
                        ? "As Input" +
                          (productType === OrderProductType.TranscodeGenericSingleFile
                            ? " (" + GetMetadataByTagName(item.assetInfos, "techFrameRate")?.value + ")"
                            : "")
                        : value.value.toString()
                    }
                    isOptionEqualToValue={(option, value) => option && option?.value === value?.value}
                    filterOptions={(x, s) => x.filter((a) => a.value?.toString().includesAllWords(s.inputValue))}
                    validationProps={{
                      formId: formId,
                      formSectionId: "sectionFileSpec",
                      fieldId: "fps",
                      required: true,
                      initialState: false,
                    }}
                  />
                </Grid>

                <Grid item md={3} display={tgop.isAudioOnly ? "none" : ""}>
                  <FormAutocomplete
                    label={t("field:label.scanFieldOrder")}
                    options={tgop.videoFieldOrder?.values!}
                    value={tgop.videoFieldOrder?.selectedValue}
                    multiple={false}
                    onChange={(v) => {
                      if (!v) {
                        handleUpdateTranscodeParam(TgopField.ScanType, null);
                        return;
                      }

                      let srcFps = GetMetadataByTagName(item.assetInfos, "techFrameRate")?.value;
                      let incorrectCfg =
                        tgop.frameRate?.selectedValue?.value === 0 &&
                        productType === OrderProductType.TranscodeGenericSingleFile &&
                        (srcFps === 23.98 || srcFps === 24);

                      if (
                        tgop.frameRate?.selectedValue !== null &&
                        tgop.frameRate?.selectedValue !== undefined &&
                        tgop.frameRate?.selectedValue.value !== null &&
                        (tgop.frameRate?.selectedValue.value === 23.98 ||
                          tgop.frameRate?.selectedValue.value === 24 ||
                          incorrectCfg) &&
                        v.fieldOrder === TranskodeFieldOrder.Interlaced
                      ) {
                        enqueueCustomSnackbar(t("alert:warning.scanFieldOrderIncompatible"), "warning");
                        handleUpdateTranscodeParam(TgopField.ScanType, undefined);
                        return;
                      }

                      handleUpdateTranscodeParam(TgopField.ScanType, v);
                    }}
                    onReset={(v) => handleUpdateTranscodeParam(TgopField.ScanType, null)}
                    getOptionLabel={(value: any) => value.fieldOrder.toString()}
                    isOptionEqualToValue={(option, value) => option && option?.fieldOrder === value?.fieldOrder}
                    filterOptions={(x, s) => x.filter((a) => a.fieldOrder?.toString().includesAllWords(s.inputValue))}
                    validationProps={{
                      formId: formId,
                      formSectionId: "sectionFileSpec",
                      fieldId: "scanType",
                      required: !tgop.isAudioOnly,
                      initialState: false,
                    }}
                  />
                </Grid>
              </Grid>
            )}

            <Stack direction="row">
              {!tgop.rewrapOnly && (
                <Typography color="text.tier">
                  <FormControlLabel
                    sx={{ mt: 1.5, ml: 0 }}
                    control={
                      <Switch
                        size="small"
                        disabled={item.assetInfos?.type === ObjectType.Audio}
                        checked={tgop.isAudioOnly}
                        onChange={() => handleToggleAudioOnly()}
                      />
                    }
                    label={t("field:label.transcodeToAudioOnlyUpperCase")}
                  />
                </Typography>
              )}
              {(item.selectedProduct?.type === OrderProductType.TranscodeGenericSingleFile ||
                item.selectedProduct?.type === OrderProductType.YoutubeChannelDelivery ||
                item.selectedProduct?.type === OrderProductType.TranscodeSequence) &&
                (!tgop.fileFormat?.selectedValue ||
                  tgop.fileFormat?.selectedValue?.codec === TranskodeVideoCodec.H264) &&
                (item.assetInfos?.type === ObjectType.Video ||
                  item.assetInfos?.type === ObjectType.TimetoolSequence) && (
                  <Typography color="text.tier">
                    <FormControlLabel
                      sx={{ mt: 1.5, ml: 0 }}
                      control={
                        <Switch
                          size="small"
                          checked={item.selectedProduct?.proxy}
                          onChange={() => handleToggleProxySource()}
                        />
                      }
                      label={t("field:label.transcodeFromProxySources")}
                    />
                  </Typography>
                )}
            </Stack>
            {tgop.fileFormat?.selectedValue?.wrapper === "WAV" &&
              tgop.fileFormat?.selectedValue?.codec !== "DolbyAtmos" &&
              tgop.isAudioOnly &&
              !tgop.rewrapOnly && (
                <Typography color="text.tier">
                  <FormControlLabel
                    sx={{ mt: 1.5, ml: 0 }}
                    control={
                      <Switch
                        size="small"
                        checked={tgop.explodeAudioChannels}
                        onChange={() => handleUpdateTranscodeParam(TgopField.ExplodeAudioChannels, null)}
                      />
                    }
                    label={t("field:label.splitAudioChannels")}
                  />
                </Typography>
              )}
            {GetMetadataByTagName(item.assetInfos, "hdrType")?.value === "DolbyVision" && (
              <Typography color="text.tier">
                <FormControlLabel
                  sx={{ mt: 1.5, ml: 0 }}
                  control={
                    <Switch
                      size="small"
                      checked={tgop.rewrapOnly}
                      onChange={() => handleUpdateTranscodeParam(TgopField.RewrapOnly, null)}
                    />
                  }
                  label={t("field:label.rewrapFileOnly")}
                />
              </Typography>
            )}
            {((tgop.frameRate?.selectedValue && tgop.frameRate?.selectedValue?.value === 29.97) ||
              tgop.frameRate?.selectedValue?.value === 59.97) &&
              !tgop.rewrapOnly && (
                <Typography color="text.tier">
                  <FormControlLabel
                    sx={{ mt: 1.5, ml: 0 }}
                    control={
                      <Switch
                        size="small"
                        checked={tgop.frameRate?.selectedValue.dropFrame ?? false}
                        onChange={() =>
                          handleUpdateTranscodeParam(TgopField.DropFrame, !tgop.frameRate!.selectedValue!.dropFrame)
                        }
                      />
                    }
                    label={t("field:label.dropFrameTimecode")}
                  />
                </Typography>
              )}
          </FormSection>
        )}
        {productType === OrderProductType.TranscodeGeneric && (
          <FormSection
            icon={<ConfigFormIcon icon="audio" />}
            title={t("common:audioMapping")}
            status={
              formSectionAudio &&
              tgop.isAudioOnly &&
              tgop.audioMixes?.selectedValues &&
              tgop.audioMixes?.selectedValues.length > 0
                ? "complete"
                : !tgop.isAudioOnly
                ? tgop.audioMixes?.selectedValues && tgop.audioMixes?.selectedValues.length > 0
                  ? "complete"
                  : "todo"
                : "warning"
            }
            statusText={audioMappingStatusText}
          >
            <FormAutocomplete
              label={t("field:label.audioMixes")}
              options={audioMixesOptions}
              value={tgop.audioMixes?.selectedValues}
              multiple={true}
              onChange={(v) => {
                handleUpdateTranscodeParam(TgopField.AudioMixes, v);
              }}
              getOptionLabel={(value: any) => {
                return GetAudioLabel(value);
              }}
              renderOption={(props, option) => {
                // Do not render option if duplicate
                if (Object.hasOwn(option, "originalId")) return;

                const count = audioMixesOptions.filter((x) => x.originalId === option.id)?.length;

                return (
                  <ListItem
                    key={option.id}
                    disablePadding
                    onClick={(e) => {
                      if (count === 0) {
                        handleUpdateTranscodeParam(TgopField.AudioMixes, [
                          ...(tgop.audioMixes?.selectedValues ?? []),
                          option,
                        ]);
                      }
                    }}
                    sx={{
                      cursor: count === 0 ? "pointer" : "default",
                      padding: 1,
                      "&:hover": {
                        backgroundColor: count === 0 ? "background.alphaHover" : "transparent",
                      },
                    }}
                    secondaryAction={
                      <Stack direction={"row"} alignItems={"center"} gap={1}>
                        <IconButton
                          size="small"
                          color="ghost"
                          sx={{
                            opacity: count > 0 ? 1 : 0,
                          }}
                          onClick={(e) => {
                            handleUpdateTranscodeParam(TgopField.AudioMixes, [
                              ...(tgop.audioMixes?.selectedValues ?? []).filter((x) => x.id !== option.id),
                            ]);
                          }}
                        >
                          <DeleteOutline />
                        </IconButton>
                        <Typography variant="body1" color="text.secondary" sx={{ width: 10, textAlign: "center" }}>
                          {count}
                        </Typography>
                        <IconButton
                          size="small"
                          color="ghost"
                          sx={{
                            opacity: count > 0 ? 1 : 0,
                          }}
                          onClick={(e) => {
                            handleUpdateTranscodeParam(TgopField.AudioMixes, [
                              ...(tgop.audioMixes?.selectedValues ?? []),
                              option,
                            ]);
                          }}
                        >
                          <Add />
                        </IconButton>
                      </Stack>
                    }
                  >
                    <Typography variant="body1">{GetAudioLabel(option)}</Typography>
                  </ListItem>
                );
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterOptions={(x, s) =>
                x.filter(
                  (a) =>
                    a.language?.value?.toString().toLowerCase().includes(s.inputValue.toLowerCase()) ||
                    a.language?.label?.toString().toLowerCase().includes(s.inputValue.toLowerCase()) ||
                    !a.language
                )
              }
              validationProps={{
                formId: formId,
                formSectionId: "sectionAudio",
                fieldId: "audiomixes",
                required: tgop.isAudioOnly,
                maxLength: 4, // 4 mixes max (32 channels in MOV)
                initialState: false,
              }}
            />
            <Typography color="text.tier">
              <FormControlLabel
                sx={{ mt: 1.5, ml: 0 }}
                control={
                  <Switch
                    size="small"
                    checked={tgop.avoidVarispeed}
                    onChange={() => handleUpdateTranscodeParam(TgopField.AudioAvoidVarispeed, null)}
                  />
                }
                label={t("field:label.avoidVarispeed")}
              />
            </Typography>
          </FormSection>
        )}

        {!simplifiedForm && !tgop.rewrapOnly && (
          <FormSection
            sx={{ display: tgop.isAudioOnly ? "none" : "" }}
            icon={<ConfigFormIcon icon="picture" />}
            title={t("common:pictureOptions")}
            status={formSectionPicture || formSectionPicture === undefined ? "complete" : "warning"}
            statusText={pictureOptionsStatusText}
          >
            {productType === OrderProductType.TranscodeGeneric && (
              <FormAutocomplete
                label={t("field:label.pictureVersion")}
                options={
                  tgop.picture?.values!.filter((x) => !toggleAvailableOnly || (toggleAvailableOnly && x.isAvailable))!
                }
                value={tgop.picture?.selectedValue}
                multiple={false}
                onChange={(v) => handleUpdateTranscodeParam(TgopField.Picture, v)}
                getOptionLabel={(option) => getOptionLabel(option as PictureOptionCouple)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                validationProps={{
                  formId: formId,
                  formSectionId: "sectionPicture",
                  fieldId: "picture",
                  required: !tgop.isAudioOnly,
                  initialState: false,
                }}
              />
            )}
            <Typography color="text.tier">
              <FormControlLabel
                sx={{ mt: 1.5, ml: 0 }}
                control={
                  <Switch
                    size="small"
                    checked={tgop.burnTimecode}
                    onChange={() => handleUpdateTranscodeParam(TgopField.BurnTimecode, null)}
                  />
                }
                label={t("field:label.printTimecodeInFile")}
              />
            </Typography>
            <Typography color="text.tier">
              <FormControlLabel
                sx={{ mt: 1.5, ml: 0 }}
                control={
                  <Switch
                    size="small"
                    checked={tgop.videoFilters?.desaturation}
                    onChange={() => handleUpdateTranscodeParam(TgopField.VideoFilterDesature, null)}
                  />
                }
                label={t("field:label.applyBlackWhiteVideoFilter")}
              />
            </Typography>
          </FormSection>
        )}
        {productType === OrderProductType.TranscodeGeneric && (
          <FormSection
            icon={<ConfigFormIcon icon="subtitles" />}
            title={t("form:section.title.subtitlingOptions")}
            status={
              formSectionSubtitle &&
              ((tgop.subtitleSidecarFiles?.selectedValues && tgop.subtitleSidecarFiles?.selectedValues.length > 0) ||
                tgop.subtitleBurned?.selectedValue)
                ? "complete"
                : !formSectionSubtitle || !isSubtitleConfigValid
                ? "warning"
                : "todo"
            }
            statusText={subtitleOptionStatusText}
          >
            <FormAutocomplete
              label={t("field:label.burnedSubtitles")}
              options={
                tgop.subtitleBurned?.values!.filter(
                  (x) => !toggleAvailableOnly || (toggleAvailableOnly && x.isAvailable)
                )!
              }
              value={tgop.subtitleBurned?.selectedValue}
              multiple={false}
              onChange={(v) => {
                if (v !== null && tgop.fileFormat?.selectedValue?.videoProfile?.includes("Custom")) {
                  enqueueCustomSnackbar(t("alert:warning.burnedSubtitlesNotSupported"), "warning");
                  return;
                }

                handleUpdateTranscodeParam(TgopField.SubtitleBurned, v);
              }}
              getOptionLabel={(value: any) => {
                return GetSubtitleLabel(value);
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterOptions={(x, s) =>
                x.filter(
                  (a) =>
                    a.language?.value?.toString().toLowerCase().includes(s.inputValue.toLowerCase()) ||
                    a.language?.label?.toString().toLowerCase().includes(s.inputValue.toLowerCase())
                )
              }
              validationProps={{
                formId: formId,
                formSectionId: "sectionSubtitle",
                fieldId: "subBurned",
                required: false,
                initialState: false,
              }}
              stackProps={{ sx: { display: tgop.isAudioOnly ? "none" : "" }, mb: 1.5 }}
            />
            <Grid container spacing={3}>
              <Grid item md={7}>
                <FormAutocomplete
                  label={t("field:label.sidecarSubtitleFiles")}
                  options={
                    tgop.subtitleSidecarFiles?.values!.filter(
                      (x) => !toggleAvailableOnly || (toggleAvailableOnly && x.isAvailable)
                    )!
                  }
                  value={tgop.subtitleSidecarFiles?.selectedValues}
                  multiple
                  onChange={(v) => {
                    handleUpdateTranscodeParam(TgopField.SubtitleSidecarFiles, v);
                    if (!v || v.length === 0) handleUpdateTranscodeParam(TgopField.SubtitleSidecarFormat, null);
                  }}
                  onReset={() => handleUpdateTranscodeParam(TgopField.SubtitleSidecarFormat, null)}
                  getOptionLabel={(value: any) => {
                    return GetSubtitleLabel(value);
                  }}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  filterOptions={(x, s) =>
                    x.filter(
                      (a) =>
                        a.language?.value?.toString().toLowerCase().includes(s.inputValue.toLowerCase()) ||
                        a.language?.label?.toString().toLowerCase().includes(s.inputValue.toLowerCase())
                    )
                  }
                  validationProps={{
                    formId: formId,
                    formSectionId: "sectionSubtitle",
                    fieldId: "subSidecar",
                    required: false,
                    initialState: false,
                  }}
                />
              </Grid>

              <Grid item md={5}>
                <FormAutocomplete
                  label={t("field:label.sidecarSubtitleFilesFormat")}
                  options={tgop.fileFormatSubtitleSidecar?.values!.filter((x) => x)!}
                  value={tgop.fileFormatSubtitleSidecar?.selectedValue}
                  multiple={false}
                  onChange={(v) => handleUpdateTranscodeParam(TgopField.SubtitleSidecarFormat, v)}
                  getOptionLabel={(value: any) => value.label}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  filterOptions={(x, s) => x.filter((a) => a.label?.toLowerCase().includes(s.inputValue.toLowerCase()))}
                  validationProps={{
                    formId: formId,
                    formSectionId: "sectionSubtitle",
                    fieldId: "subSidecarFormat",
                    required:
                      tgop.subtitleSidecarFiles?.selectedValues !== null &&
                      tgop.subtitleSidecarFiles!.selectedValues!.length > 0,
                    initialState: false,
                  }}
                />

                {!isSubtitleConfigValid && (
                  <Alert sx={{ mt: 1 }} color="warning" icon={<ErrorOutlineIcon />}>
                    {t("alert:warning.sourceFileNotCompatible")}
                  </Alert>
                )}
              </Grid>
            </Grid>
          </FormSection>
        )}

        {item.objectLayer !== ObjectLayer.Clip &&
          item.selectedProduct?.type !== OrderProductType.TranscodeSequence &&
          !tgop.rewrapOnly && <ConfigCommonSectionFormClips item={item} clips={clips} />}

        <ConfigCommonSectionFormDelivery item={item} formid={formId} storages={storages} maillists={maillists} />
        <ConfigCommonSectionFormScheduling item={item} formid={formId} />
        <FormSection
          icon={<ConfigFormIcon icon="security" />}
          title={t("form:section.title.securityOverlayOption")}
          status={
            formSectionSecurity && item.selectedProduct!.watermarkTemplate
              ? "complete"
              : item.selectedProduct!.watermarkTemplate ||
                item.selectedProduct?.type === OrderProductType.DcpReferenceFileSimple ||
                item.selectedProduct?.type === OrderProductType.DcpReferenceFileStitched
              ? "warning"
              : "todo"
          }
          statusText={securityOptionsStatusText}
          sx={{ display: tgop.isAudioOnly ? "none" : "" }}
        >
          <Grid container spacing={3}>
            <Grid item md={5}>
              <FormAutocomplete
                label={t("field:label.overlayTempate")}
                options={Object.values(WatermarkTemplates)}
                value={item.selectedProduct!.watermarkTemplate}
                multiple={false}
                onChange={(v) => {
                  handleUpdateProductRoot(ProductField.WatermarkTextTemplate, v);

                  if (!v) handleUpdateTranscodeParam(TgopField.OverlayText, "");
                }}
                onReset={(v) => {
                  // Non executed... strange, must use onChange
                  handleUpdateProductRoot(ProductField.WatermarkTextTemplate, null);
                  handleUpdateTranscodeParam(TgopField.OverlayText, null);
                }}
                getOptionLabel={(value: any) => value.toString().replaceAll("_", " ")}
                isOptionEqualToValue={(option, value) => option === value}
                filterOptions={(x, s) =>
                  x.filter((a) => a.toString().toLowerCase().includes(s.inputValue.toLowerCase()))
                }
                validationProps={{
                  formId: formId,
                  formSectionId: "sectionSecurity",
                  fieldId: "watermarkTemplate",
                  required:
                    item.selectedProduct?.type === OrderProductType.DcpReferenceFileSimple ||
                    item.selectedProduct?.type === OrderProductType.DcpReferenceFileStitched,
                  initialState: false,
                }}
              />
            </Grid>
            <Grid item md={7}>
              <FormInputText
                label={t("field:label.burnedTextIntoVideo")}
                startAdornment={<ChatOutlinedIcon />}
                placeholder={t("field:label.burnedTextIntoVideo")}
                value={
                  (productType === OrderProductType.TranscodeGeneric
                    ? tgop.overlayTextPlaceholder
                    : item.selectedProduct?.settings.overlayTextPlaceholder) ?? ""
                }
                onChange={(v) => handleUpdateTranscodeParam(TgopField.OverlayText, v.target.value)}
                validationProps={{
                  formId: formId,
                  formSectionId: "sectionSecurity",
                  fieldId: "watermarkText",
                  initialState: false,
                  required:
                    item.selectedProduct!.watermarkTemplate !== null ||
                    item.selectedProduct?.type === OrderProductType.DcpReferenceFileSimple ||
                    item.selectedProduct?.type === OrderProductType.DcpReferenceFileStitched,
                  minLength: item.selectedProduct!.watermarkTemplate !== null ? 3 : 0,
                }}
                disabled={!item.selectedProduct!.watermarkTemplate}
              />
            </Grid>

            {((productType === OrderProductType.TranscodeGeneric && tgop.overlayTextPlaceholder) ||
              (productType === OrderProductType.TranscodeGenericSingleFile &&
                item.selectedProduct?.settings.overlayTextPlaceholder)) && (
              <>
                <Button
                  sx={{ ml: 3, mt: 1 }}
                  fullWidth={false}
                  size="small"
                  onClick={applyToAllOverlayText}
                  variant="outlined"
                >
                  {t("button:applyToAllItems")}
                </Button>
              </>
            )}
          </Grid>
        </FormSection>

        <ConfigCommonSectionFormNexguardWatermark item={item} />

        {UserHasAcl(AclTypes.aclOrderIngestGenerated) && !simplifiedForm && (
          <ConfigCommonSectionFormAssetsVisible item={item} />
        )}

        {item.selectedProduct?.type !== OrderProductType.DcpReferenceFileSimple &&
          item.selectedProduct?.type !== OrderProductType.DcpReferenceFileStitched && (
            <ConfigCommonSectionFormStitching item={item} />
          )}
      </Stack>
    </form>
  );
};

export default ConfigTranscodeForm;
